import {
  Flex,
  Text,
  Icon,
  Link,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react'
import { IconType } from 'react-icons'
import { NavLink as RouterNavLink, useLocation } from 'react-router-dom'
import type { SidebarTransitionPhase } from './Sidebar'

interface NavItemProps {
  icon: IconType
  children: React.ReactNode
  path: string
  onClick?: () => void
  isCollapsed?: boolean
  transitionPhase?: SidebarTransitionPhase
}

export function NavItem({
  icon,
  children,
  path,
  onClick,
  isCollapsed = false,
  transitionPhase = isCollapsed ? 'collapsed' : 'expanded',
}: NavItemProps) {
  const location = useLocation()
  const targetPathname = path.split('?')[0]
  const isActive =
    location.pathname === targetPathname ||
    location.pathname.startsWith(`${targetPathname}/`)
  const labelsExpanded = transitionPhase === 'expanded' || transitionPhase === 'expanding'
  const labelsVisible = transitionPhase === 'expanded'
  
  const activeBg = useColorModeValue('admin.50', 'admin.900')
  const activeColor = useColorModeValue('admin.700', 'admin.200')
  const hoverBg = useColorModeValue('gray.100', 'gray.700')

  const navContent = (
    <Link
      as={RouterNavLink}
      to={path}
      aria-current={isActive ? 'page' : undefined}
      style={{ textDecoration: 'none' }}
      _focus={{ boxShadow: 'none' }}
      onClick={onClick}
    >
      <Flex
        align="center"
        justify={isCollapsed ? "center" : "flex-start"}
        py="1.5"
        px={isCollapsed ? "2" : "2.5"}
        mx={isCollapsed ? "1" : "1.5"}
        borderRadius="md"
        role="group"
        cursor="pointer"
        bg={isActive ? activeBg : 'transparent'}
        color={isActive ? activeColor : 'inherit'}
        _hover={{
          bg: isActive ? activeBg : hoverBg,
        }}
        minH="8"
      >
        <Icon
          mr={isCollapsed ? "0" : "3"}
          fontSize="13"
          as={icon}
        />
        <Text
          fontWeight={isActive ? 'semibold' : 'normal'}
          fontSize="xs"
          noOfLines={1}
          maxW={labelsExpanded ? '180px' : '0'}
          overflow="hidden"
          opacity={labelsVisible ? 1 : 0}
          transform={labelsVisible ? 'translateX(0)' : 'translateX(-6px)'}
          transition="max-width 0.24s ease, opacity 0.16s ease, transform 0.24s ease"
          whiteSpace="nowrap"
        >
          {children}
        </Text>
      </Flex>
    </Link>
  )

  // Wrap with Tooltip if collapsed
  if (isCollapsed) {
    return (
      <Tooltip 
        label={children} 
        placement="right" 
        hasArrow
        openDelay={300}
      >
        {navContent}
      </Tooltip>
    )
  }

  return navContent
}
